import { Injectable } from '@angular/core';
import { CONSTANTS } from 'src/app/config/constants';
import { IRoute } from 'src/app/interface/route';
import { ToastService } from 'src/app/share/toast/toast.service';
import { IGeofence, ISite } from 'src/app/interface/site';
import { LocalNotifications } from '@capacitor/local-notifications';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { environment } from 'src/environments/environment';
import { CapacitorHttp } from '@capacitor/core';
import { LocalStorage } from 'src/app/class/local-storage';

@Injectable({
  providedIn: 'root',
})
export class GeofenceNotificationsService {
  private notified: boolean = false;
  private isBackground: boolean = false;

  constructor(
    private toastService: ToastService,
    private translateService: TranslateService,
    private router: Router,
    private authService: AuthService
  ) {
    this.setupLocalNotificationsListener();
  }

  private setupLocalNotificationsListener() {
    LocalNotifications.addListener(
      'localNotificationActionPerformed',
      (payload) => {
        if (
          payload.notification?.extra?.route &&
          payload.notification?.extra?.site
        ) {
          this.router.navigate(['/auth/route/show'], {
            queryParams: {
              route_id: payload.notification.extra.route.id,
            },
          });
        }
      }
    );
  }

  public async checkLocation(lat: number, long: number) {
    if (!this.isEnabled()) {
      return;
    }

    if (this.checkLastNotification()) {
      localStorage.setItem(CONSTANTS.localStorage.keys.geofenceLastDetection, new Date().toString());

      let geolocation = await this.getRoutesGeolocations();

      if (!geolocation?.routes?.length) {
        return;
      }

      for (let i = 0; i < geolocation?.routes?.length; i++) {
        for (let j = 0; j < geolocation?.routes[i]?.sites.length; j++) {
          if (
            !geolocation?.routes[i]?.sites[j]?.geofence ||
            !geolocation?.routes[i]?.sites[j]?.geofence?.coordinates
          ) {
            continue;
          }

          if (
            this.isPointInPolygon(
              geolocation?.routes[i]?.sites[j]?.geofence,
              lat,
              long
            )
          ) {
            this.notify(
              geolocation?.routes[i],
              geolocation?.routes[i]?.sites[j]
            );
            this.notified = true;
            break;
          }
        }
        if (this.notified) {
          this.notified = false;
          break;
        }
      }
    }
  }

  private async notify(route: IRoute, site: ISite) {
    if (this.isBackground) {
      LocalNotifications.schedule({
        notifications: [
          {
            id: 78,
            title: this.translateService.instant('Geofence Detection'),
            body: this.translateService.instant(
              'Looks like you are inside in SITE_NAME without starting on the app',
              { SITE_NAME: site?.name_for_operators ?? site?.name }
            ),
            extra: { route, site },
          },
        ],
      });
    } else {
      this.toastService.toast(
        this.translateService.instant(
          'Looks like you are inside in SITE_NAME without starting on the app',
          { SITE_NAME: site?.name_for_operators ?? site?.name }
        ),
        {
          duration: 120000,
          autoDismiss: false,
        }
      );
    }
  }

  public cancelNotifications(): Promise<any> {
    LocalNotifications.cancel({ notifications: [{ id: 78 }] });
    // Reinforced attempt
    return LocalNotifications.getPending().then((pending) => {
      pending.notifications?.forEach((notification) => {
        if (notification?.id === 78) {
          LocalNotifications.cancel({
            notifications: [{ id: notification?.id }],
          });
        }
      });
      // Reinforced attempt
      if (!pending?.notifications?.length) {
        return;
      }
      LocalNotifications.cancel(pending);
    });
  }

  public setIsBackground(isBackground: boolean) {
    this.isBackground = isBackground;
  }

  private isPointInPolygon(polygon: IGeofence, lat: number, long: number) {
    var x = lat,
      y = long;

    if (!polygon?.coordinates || !polygon?.coordinates.length) {
      return false;
    }

    const coordinates = polygon.coordinates[0];

    var inside = false;
    for (
      var i = 0, j = coordinates.length - 1;
      i < coordinates.length;
      j = i++
    ) {
      var xi = coordinates[i][0],
        yi = coordinates[i][1];
      var xj = coordinates[j][0],
        yj = coordinates[j][1];

      var intersect =
        yi > y != yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }

    return inside;
  }

  private checkLastNotification() {
    const now = new Date();
    const lastNotificationDate = this.getLastNotificationDate();
    lastNotificationDate.setMinutes(lastNotificationDate.getMinutes() + 10);
    return now > lastNotificationDate;
  }

  private getLastNotificationDate() {
    const lastNotificationDate = localStorage.getItem(CONSTANTS.localStorage.keys.geofenceLastDetection);
    if (!lastNotificationDate) {
      localStorage.setItem(CONSTANTS.localStorage.keys.geofenceLastDetection, new Date().toString());
    }
    return new Date(localStorage.getItem(CONSTANTS.localStorage.keys.geofenceLastDetection));
  }

  private isEnabled() {
    const notify = localStorage.getItem(
      CONSTANTS.localStorage.keys.geofenceDetection
    );

    const notificationEnabled = LocalStorage.get(CONSTANTS.localStorage.keys.locationDisclosureAccepted) === 1;

    return (
      notify === '1' ||
      notify === null ||
      notify === undefined ||
      notify === '' ||
      notify === 'true'
    ) && notificationEnabled;
  }

  private async getRoutesGeolocations() {
    const company = this.authService.company;
    if (!company) {
      return null;
    }
    let token = this.authService.token;
    if (!token) {
      return null;
    }
    token = 'Bearer ' + token;
    let url = environment.apiUrl.replace('{language}', 'en');
    url += '/';
    url += company.id;
    url += '/geolocation/status';
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: token,
    };
    const response = await CapacitorHttp.get({
      url,
      headers,
    });

    if (response.status === 200) {
      return response.data;
    } else {
      return null;
    }
  }
}
